import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Controller, useForm } from "react-hook-form"
import {
  Button,
  FormLabel,
  LoadingSpinner,
  Modal,
  MultiSelect,
  TextArea,
  TextInput,
  FileUpload,
} from "@oysterhr/ui"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { FileStatus, MAX_FILE_SIZE_MB } from "~/shared/hooks/useFileUploadComponent/utils"
import { DocumentProps, AddDocumentModalProps } from "~/app/components/AddDocumentModal/types"
import { useFileUploadComponent } from "~/shared/hooks/useFileUploadComponent"
import { downloadLinkToFileUpload } from "@oysterhr/hiring-contractors"

const initialValues = { title: "", description: "", signedDocumentId: "", filename: "" }

function AddDocumentModal({
  onSubmit,
  setShowModal,
  initialFile,
  mimeTypes,
  tags = [],
  fileUploadAssistiveText = "",
}: AddDocumentModalProps) {
  const { t } = useTranslation("components/AddDocumentModal")

  const defaultValues = initialFile || initialValues

  const { files, handleChange, handleRemoveFile } = useFileUploadComponent(
    {
      maxSize: MAX_FILE_SIZE_MB,
      multiple: false,
    },
    initialFile
      ? [
          downloadLinkToFileUpload({
            ...initialFile,
            documentId: initialFile.signedDocumentId,
          }),
        ]
      : undefined,
  )

  const validationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string().required(t("required.fileName")),
        description: Yup.string().required(t("required.description")),
        signedDocumentId: Yup.string().required(t("required.file")),
        tags:
          tags.length > 0
            ? Yup.array()
                .of(Yup.string().required(t("required.tags")))
                .min(1, t("required.tags"))
                .required(t("required.tags"))
            : Yup.mixed().optional(),
      }),
    [t, tags.length],
  )

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DocumentProps>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  return (
    <Modal isOpen onRequestClose={() => setShowModal(false)} data-testid="add-document-modal">
      <Modal.Header>{t("title")}</Modal.Header>
      <Modal.Content>
        <form
          id="add-document-modal"
          onSubmit={e => {
            // prevent the page to refresh on submit
            e.preventDefault()
            // prevent submitting parent form (for example, in staff view onboarding form)
            e.stopPropagation()
            handleSubmit(onSubmit)()
          }}
        >
          <div className="mb-4">
            <TextInput
              {...register("title")}
              label={<FormLabel label={t("fileName")} />}
              error={errors?.title?.message}
            />
          </div>

          <div className="mb-4">
            <TextArea
              {...register("description")}
              label={<FormLabel label={t("fileDescription")} />}
              placeholder={t("placeholder.fileDescription")}
              error={errors?.description?.message}
            />
          </div>

          {tags.length > 0 && (
            <div className="mb-4">
              <Controller
                control={control}
                name="tags"
                render={({ field: { value, onChange, name }, fieldState: { error } }) => {
                  return (
                    <MultiSelect
                      id={name}
                      value={value}
                      onChange={onChange}
                      label={<FormLabel label={t("tags")} />}
                      error={error?.message}
                    >
                      {tags.map(({ label, value }) => (
                        <MultiSelect.Option key={value} value={value}>
                          {label}
                        </MultiSelect.Option>
                      ))}
                    </MultiSelect>
                  )
                }}
              />
            </div>
          )}

          <Controller
            name="signedDocumentId"
            control={control}
            render={({ field: { name, onChange }, fieldState: { error } }) => (
              <FileUpload
                accept={mimeTypes}
                name={name}
                error={error?.message}
                label={
                  <FormLabel label={t("uploadFile")} assistiveText={fileUploadAssistiveText} />
                }
                onChange={handleChange(([file]) => {
                  onChange(file?.signedId)
                  setValue("filename", file?.file?.name || "")
                })}
              >
                {files.map(({ id, file, href, status, error }) => (
                  <FileUpload.Item
                    key={id || href || file.name} // fallback in case of missing id or href
                    href={href}
                    error={error}
                    isLoading={[FileStatus.Idle, FileStatus.Uploading].includes(status)}
                    onRemove={() => {
                      handleRemoveFile(() => {
                        onChange(undefined)
                        setValue("filename", "")
                      })(id)
                    }}
                  >
                    {file.name}
                  </FileUpload.Item>
                ))}
              </FileUpload>
            )}
          />

          <div className="mt-4 flex items-center gap-3 md:justify-end">
            <Button type="button" variation="secondary" onClick={() => setShowModal(false)}>
              {t("cancel")}
            </Button>
            <Button
              form="add-document-modal"
              variation="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner size="md" /> : t("addDocument")}
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  )
}

export default AddDocumentModal
